import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Button,
  Input,
  SearchBox,
  Toolbar,
  ToolbarGroup,
  ToolbarButton,
  Checkbox,
  Spinner,
  Text,
  tokens,
  Card,
  CardHeader,
  Title2,
  Subtitle2,
  Tooltip,
  MessageBar,
  MessageBarBody,
  Field,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Dropdown,
  Option,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
} from '@fluentui/react-components';
import {
  Eye20Regular,
  Edit20Regular,
  Add20Regular,
  Dismiss20Regular,
  ArrowUp20Regular,
  ArrowDown20Regular,
} from '@fluentui/react-icons';
import { apiConfig } from '../authConfig';
import { useAuth } from '../hooks/useAuth';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { POLARIS_CARD_CAPTION_PADDING_Y } from './polarisSharedStyles';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.padding('0'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    ...shorthands.padding(tokens.spacingHorizontalM),
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  listSection: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  searchInput: {
    width: '220px',
  },
  toolbar: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: tokens.shadow2,
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  toolbarFarGroup: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  loading: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 0,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  cellContent: {
    display: 'block',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
  },
  headerRow: {
    backgroundColor: tokens.colorBrandBackground,
    '& > :nth-child(1):hover, & > :nth-child(2):hover, & > :nth-child(1):focus-within, & > :nth-child(2):focus-within': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '& > :nth-child(1):active, & > :nth-child(2):active': {
      backgroundColor: 'rgba(255, 255, 255, 0.14)',
    },
    '& > :nth-child(1) [role="button"], & > :nth-child(2) [role="button"]': {
      backgroundColor: 'transparent',
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
    },
    '& > :nth-child(1) [role="button"]:hover, & > :nth-child(2) [role="button"]:hover, & > :nth-child(1) [role="button"]:active, & > :nth-child(2) [role="button"]:active, & > :nth-child(1) [role="button"]:focus-visible, & > :nth-child(2) [role="button"]:focus-visible': {
      backgroundColor: 'transparent',
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
    },
    '& > :nth-child(1) [role="button"] *, & > :nth-child(2) [role="button"] *': {
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
    },
  },
  headerCell: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    minWidth: 0,
    overflow: 'hidden',
    ...shorthands.padding(0, tokens.spacingHorizontalSNudge),
  },
  dataRow: {
    fontSize: tokens.fontSizeBase200,
    ':nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      cursor: 'pointer',
    },
  },
  disabledRow: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground4,
    ':nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground4,
    },
  },
  dataCell: {
    fontSize: tokens.fontSizeBase200,
    minWidth: 0,
    overflow: 'hidden',
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalSNudge),
  },
  countInfo: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  listCardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    ...shorthands.gap(tokens.spacingHorizontalM),
    width: '100%',
    ...shorthands.padding(POLARIS_CARD_CAPTION_PADDING_Y, tokens.spacingHorizontalM),
  },
  listCardHeaderSpacer: {
    flex: 1,
  },
  listCardBody: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    ...shorthands.padding('0'),
  },
  listScrollBody: {
    flex: 1,
    minHeight: 0,
    overflowX: 'clip',
    overflowY: 'auto',
    position: 'relative',
    contain: 'layout style paint',
  },
  listCard: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    ...shorthands.padding('0'),
    ...shorthands.gap('0'),
    rowGap: '0',
    columnGap: '0',
    '& .ms-Card-content': {
      ...shorthands.padding('0'),
    },
    '& > div': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
    '& [class*="CardHeader"]': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
    '& [class*="ms-Card-header"]': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
  },
  detailTopRow: {
    display: 'flex',
    ...shorthands.gap('1px'),
    minWidth: 0,
  },
  detailColumn: {
    flex: 1,
    minWidth: 0,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '160px minmax(0, 1fr)',
    fontSize: tokens.fontSizeBase200,
    ...shorthands.gap('1px'),
    minWidth: 0,
  },
  detailLabel: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.padding('4px', '8px'),
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    fontSize: '12px',
  },
  detailValue: {
    ...shorthands.padding('4px', '8px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    wordBreak: 'break-all',
    fontSize: '12px',
  },
  detailFormGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalL),
    alignItems: 'stretch',
  },
  detailSectionCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  detailSectionBody: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  detailFieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
  },
  detailCheckboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalS),
  },
  detailValueText: {
    display: 'block',
    minWidth: 0,
    wordBreak: 'break-word',
  },
  detailReadonlyInput: {
    width: '100%',
  },
  settingsTable: {
    width: '100%',
    minWidth: '960px',
    borderCollapse: 'collapse',
    fontSize: '12px',
    marginTop: '4px',
    tableLayout: 'auto',
  },
  settingsTableContainer: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  settingsHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.padding('4px', '8px'),
    textAlign: 'left',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  settingsTd: {
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    verticalAlign: 'top',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  settingsKeyColumn: {
    minWidth: '220px',
    width: '220px',
    whiteSpace: 'nowrap',
  },
  pageTitle: {
    marginBottom: '12px',
  },
  actionGroupCompact: {
    display: 'flex',
    ...shorthands.gap('4px'),
  },
  actionGroupWrap: {
    display: 'flex',
    ...shorthands.gap('4px'),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  messageBarCompact: {
    marginBottom: '4px',
  },
  cardTopMargin: {
    marginTop: '12px',
  },
  fullWidthField: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  fullWidthDropdownField: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    '& > button': {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    },
  },
  emptyStateText: {
    ...shorthands.padding('0', tokens.spacingHorizontalM, tokens.spacingVerticalM, tokens.spacingHorizontalM),
    display: 'block',
    color: tokens.colorNeutralForeground4,
  },
  headerActions: {
    display: 'flex',
    ...shorthands.gap('8px'),
    alignItems: 'center',
  },
  errorBanner: {
    marginBottom: '8px',
  },
  dataGridText: {
    fontSize: '12px',
  },
  dataGridCompact: {
    fontSize: tokens.fontSizeBase200,
  },
  stickyGridHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  detailScrollContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    ...shorthands.padding(tokens.spacingHorizontalM),
    boxSizing: 'border-box',
  },
  detailTopSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  defaultSettingsCard: {
    flexShrink: 0,
  },
});

function TestTypeDetail({ testType, onBack, getAccessToken, onTestTypeUpdated }) {
  const classes = useStyles();
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [extraKeys, setExtraKeys] = useState([]);
  const [deletedKeys, setDeletedKeys] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const [metaEditing, setMetaEditing] = useState(false);
  const [metaValues, setMetaValues] = useState({});
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaError, setMetaError] = useState(null);
  const [metaSuccess, setMetaSuccess] = useState(null);

  const [resultEditing, setResultEditing] = useState(false);
  const [resultLabel, setResultLabel] = useState('');
  const [resultCounters, setResultCounters] = useState([]);
  const [resultSaving, setResultSaving] = useState(false);
  const [resultError, setResultError] = useState(null);
  const [resultSuccess, setResultSuccess] = useState(null);

  const latestResultQuery = useQuery({
    queryKey: ['polaris-test-type-latest-result', testType.Id],
    queryFn: async () => {
      const token = await getAccessToken();
      const resp = await fetch(
        `${apiConfig.apiBaseUrl}/polaris/test-types/${encodeURIComponent(testType.Id)}/latest-result`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error(`API error: ${resp.status}`);
      return resp.json();
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const latestCounters = latestResultQuery.data?.counters || null;

  const defaultSettingsQuery = useQuery({
    queryKey: ['polaris-test-type-default-settings', testType.Id],
    queryFn: async () => {
      const token = await getAccessToken();
      const resp = await fetch(
        `${apiConfig.apiBaseUrl}/polaris/test-types/${encodeURIComponent(testType.Id)}/default-settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error(`API error: ${resp.status}`);
      const data = await resp.json();
      const map = {};
      if (Array.isArray(data)) {
        for (const row of data) {
          if (row.Key) map[row.Key] = row.Value ?? '';
        }
      }
      return map;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const defaultValues = defaultSettingsQuery.data ?? {};
  const fetchDefaultSettings = useCallback(
    () => defaultSettingsQuery.refetch(),
    [defaultSettingsQuery]
  );

  const leftFields = [
    ['Id', testType.Id],
    ['TaskType', testType.TaskType],
    ['Owner', testType.Owner],
    ['Group', testType.Group],
    ['MachineType', testType.MachineType],
    ['QueueTimeout', testType.QueueTimeout ?? 'Optional'],
    ['RunTimeout', testType.RunTimeout ?? 'Optional'],
    ['DualBase', testType.DualBase],
    ['TestLogRootPath', testType.TestLogRootPath || 'Optional'],
    ['IsCompliance', testType.IsCompliance],
    ['MultiQueueConfig', testType.MultiQueueConfig],
    ['FlipBedRule', testType.FlipBedRule || 'Optional'],
  ];

  const rightFields = [
    ['IsEnabled', testType.IsEnabled],
    ['EnvType', testType.EnvType],
    ['ArgumentsMapping', testType.ArgumentsMapping || 'Optional'],
    ['EnableFrozen', testType.EnableFrozen],
    ['MachineCount', testType.MachineCount],
    ['EnableFailover', testType.EnableFailover],
    ['EnableMultiMachines', testType.EnableMultiMachines],
    ['EnableDegradeLogic', testType.EnableDegradeLogic],
    ['LogReaderSecurityGroup', testType.LogReaderSecurityGroup || 'Optional'],
    ['RunMode', testType.RunMode],
    ['ExternalMachinePool', testType.ExternalMachinePool || 'Optional'],
    ['ServiceLogLink', testType.ServiceLogLink || 'Optional'],
  ];

  const requiredDetailFields = [
    ['Id', testType.Id],
    ['TaskType', testType.TaskType],
    ['Owner', testType.Owner],
    ['MachineType', testType.MachineType],
  ];

  const executionDetailFields = [
    ['MachineCount', testType.MachineCount],
    ['EnvType', testType.EnvType],
    ['RunMode', testType.RunMode],
    ['MultiQueueConfig', testType.MultiQueueConfig],
    ['QueueTimeout', testType.QueueTimeout ?? ''],
    ['RunTimeout', testType.RunTimeout ?? ''],
  ];

  const optionalDetailFields = [
    ['Group', testType.Group],
    ['TestLogRootPath', testType.TestLogRootPath || ''],
    ['LogReaderSecurityGroup', testType.LogReaderSecurityGroup || ''],
    ['ExternalMachinePool', testType.ExternalMachinePool || ''],
    ['ArgumentsMapping', testType.ArgumentsMapping || ''],
    ['FlipBedRule', testType.FlipBedRule || ''],
    ['ServiceLogLink', testType.ServiceLogLink || ''],
  ];

  const featureFlagFields = [
    ['IsEnabled', testType.IsEnabled],
    ['EnableFrozen', testType.EnableFrozen],
    ['EnableMultiMachines', testType.EnableMultiMachines],
    ['EnableDegradeLogic', testType.EnableDegradeLogic],
    ['DualBase', testType.DualBase],
    ['IsCompliance', testType.IsCompliance],
    ['EnableFailover', testType.EnableFailover],
  ];

  const renderValue = (val) => {
    if (typeof val === 'boolean') return <Checkbox checked={val} disabled />;
    return (
      <Input
        className={classes.detailReadonlyInput}
        value={String(val ?? '')}
        readOnly
      />
    );
  };

  const META_BOOL_FIELDS = ['IsEnabled', 'EnableFrozen', 'EnableMultiMachines', 'EnableDegradeLogic', 'DualBase', 'IsCompliance', 'EnableFailover'];
  const META_NUMBER_FIELDS = ['MachineCount', 'MultiQueueConfig', 'QueueTimeout', 'RunTimeout'];
  const META_DROPDOWN_FIELDS = {
    EnvType: ['Polaris', 'PolarisTip', 'PolarisBox'],
    RunMode: ['Iteration', 'Dynamic', 'Throughput', 'MultiResult'],
  };

  const startMetaEditing = () => {
    setMetaValues({
      TaskType: testType.TaskType || '',
      Owner: testType.Owner || '',
      MachineType: testType.MachineType || '',
      MachineCount: testType.MachineCount ?? 1,
      EnvType: testType.EnvType || '',
      IsEnabled: testType.IsEnabled ?? false,
      EnableFrozen: testType.EnableFrozen ?? false,
      EnableMultiMachines: testType.EnableMultiMachines ?? false,
      EnableDegradeLogic: testType.EnableDegradeLogic ?? false,
      DualBase: testType.DualBase ?? false,
      IsCompliance: testType.IsCompliance ?? false,
      RunMode: testType.RunMode || '',
      EnableFailover: testType.EnableFailover ?? false,
      MultiQueueConfig: testType.MultiQueueConfig ?? -1,
      Group: testType.Group || '',
      QueueTimeout: testType.QueueTimeout ?? '',
      RunTimeout: testType.RunTimeout ?? '',
      TestLogRootPath: testType.TestLogRootPath || '',
      LogReaderSecurityGroup: testType.LogReaderSecurityGroup || '',
      ExternalMachinePool: testType.ExternalMachinePool || '',
      ArgumentsMapping: testType.ArgumentsMapping || '',
      FlipBedRule: testType.FlipBedRule || '',
      ServiceLogLink: testType.ServiceLogLink || '',
    });
    setMetaEditing(true);
    setMetaError(null);
    setMetaSuccess(null);
  };

  const cancelMetaEditing = () => {
    setMetaEditing(false);
    setMetaValues({});
    setMetaError(null);
  };

  const handleMetaSave = async () => {
    setMetaSaving(true);
    setMetaError(null);
    setMetaSuccess(null);
    try {
      const token = await getAccessToken();
      const payload = { ...metaValues };
      if (payload.QueueTimeout === '') payload.QueueTimeout = null;
      else payload.QueueTimeout = parseInt(payload.QueueTimeout, 10);
      if (payload.RunTimeout === '') payload.RunTimeout = null;
      else payload.RunTimeout = parseInt(payload.RunTimeout, 10);
      payload.MachineCount = parseInt(payload.MachineCount, 10);
      payload.MultiQueueConfig = parseFloat(payload.MultiQueueConfig);

      const resp = await fetch(
        `${apiConfig.apiBaseUrl}/polaris/test-types/${encodeURIComponent(testType.Id)}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to update test type: ${resp.status}`);
      }
      setMetaEditing(false);
      setMetaSuccess('Test type updated successfully.');
      if (onTestTypeUpdated) onTestTypeUpdated({ ...testType, ...payload });
    } catch (err) {
      setMetaError(err.message || 'Failed to update test type. Please try again.');
    } finally {
      setMetaSaving(false);
    }
  };

  const renderMetaField = (label, value) => {
    if (!metaEditing) {
      return renderValue(value);
    }
    const key = label;
    if (META_BOOL_FIELDS.includes(key)) {
      return (
        <Checkbox
          checked={metaValues[key] ?? false}
          onChange={(_e, d) => setMetaValues(prev => ({ ...prev, [key]: d.checked }))}
        />
      );
    }
    if (META_DROPDOWN_FIELDS[key]) {
      return (
        <Dropdown
          className={classes.fullWidthField}
          value={metaValues[key] ?? ''}
          selectedOptions={metaValues[key] ? [metaValues[key]] : []}
          onOptionSelect={(_e, d) => setMetaValues(prev => ({ ...prev, [key]: d.optionValue }))}
        >
          {META_DROPDOWN_FIELDS[key].map(o => <Option key={o} value={o}>{o}</Option>)}
        </Dropdown>
      );
    }
    if (META_NUMBER_FIELDS.includes(key)) {
      return (
        <Input
          className={classes.fullWidthField}
          type="number"
          value={String(metaValues[key] ?? '')}
          onChange={(_e, d) => setMetaValues(prev => ({ ...prev, [key]: d.value }))}
          {...(key === 'MachineCount' ? { min: 0, max: 9 } : {})}
        />
      );
    }
    return (
      <Input
        className={classes.fullWidthField}
        value={metaValues[key] ?? ''}
        onChange={(_e, d) => setMetaValues(prev => ({ ...prev, [key]: d.value }))}
      />
    );
  };

  const renderDetailField = (label, value) => (
    <Field key={label} label={label}>
      {label === 'Id' ? renderValue(value) : renderMetaField(label, value)}
    </Field>
  );

  const renderDetailCheckboxField = (label, value) => {
    if (metaEditing) {
      return (
        <Field key={label}>
          <Checkbox
            label={label}
            checked={metaValues[label] ?? false}
            onChange={(_e, d) => setMetaValues(prev => ({ ...prev, [label]: d.checked }))}
          />
        </Field>
      );
    }

    return (
      <Field key={label}>
        <Checkbox label={label} checked={value ?? false} disabled />
      </Field>
    );
  };

  const startEditing = () => {
    setEditValues({ ...defaultValues });
    setExtraKeys([]);
    setDeletedKeys([]);
    setCustomKeys([]);
    const hiddenMap = {};
    const structuredMap = {};
    const helpMap = {};
    const aliasMap = {};
    for (const s of defaultSettings) {
      hiddenMap[s.key] = s.Hidden ?? false;
      structuredMap[s.key] = s.Structured ?? false;
      helpMap[s.key] = s.Help || '';
      aliasMap[s.key] = s.Alias || '';
    }
    setEditHidden(hiddenMap);
    setEditStructured(structuredMap);
    setEditHelp(helpMap);
    setEditAlias(aliasMap);
    setEditing(true);
    setSaveError(null);
    setSaveSuccess(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditValues({});
    setExtraKeys([]);
    setDeletedKeys([]);
    setCustomKeys([]);
    setEditHidden({});
    setEditStructured({});
    setEditHelp({});
    setEditAlias({});
    setSaveError(null);
  };

  const BUILTIN_PARAMS = ['PerfFlag', 'QPSList', 'DPSList', 'WarmupQPS', 'EnableFPGA', 'EnableBorrowMachine'];

  const PERFFLAG_OPTIONS = [
    'Default', 'Logman', 'F1', 'XPerf', 'F1Lock', 'Customer',
    'APCounter', 'F1_Logman', 'PyCounter', 'CockpitMetrics',
    'JarvisMetrics', 'AMDuProf', 'Prometheus',
  ];

  const AIRFLOW_PERFFLAG_OPTIONS = [
    'default',
    'dotnetTraceLinux',
    'perfviewWindowsDotnet',
    'perfviewWindows',
    'perfLinux',
    'jfrLinux',
    'asyncProfilerLinux',
    'pyspyLinux',
  ];

  const DROPDOWN_PARAMS = {
    EnableFPGA: ['0', '1'],
    EnableBorrowMachine: ['Yes', 'No'],
  };

  const renderEditInput = (key) => {
    const options = key === 'PerfFlag'
      ? (testType.MachineType === 'Airflow'
        ? AIRFLOW_PERFFLAG_OPTIONS
        : PERFFLAG_OPTIONS)
      : DROPDOWN_PARAMS[key];
    if (options) {
      return (
        <Dropdown
          className={mergeClasses(classes.fullWidthField, classes.fullWidthDropdownField)}
          value={editValues[key] ?? ''}
          selectedOptions={editValues[key] ? [editValues[key]] : []}
          onOptionSelect={(_e, d) => setEditValues(prev => ({ ...prev, [key]: d.optionValue || '' }))}
        >
          {options.map(o => <Option key={o} value={o}>{o}</Option>)}
        </Dropdown>
      );
    }
    return (
      <Input
        className={classes.fullWidthField}
        value={editValues[key] ?? ''}
        onChange={(_e, d) => setEditValues(prev => ({ ...prev, [key]: d.value }))}
      />
    );
  };

  const addBuiltinParam = (key) => {
    setExtraKeys(prev => [...prev, key]);
    setDeletedKeys(prev => prev.filter(k => k !== key));
    setEditValues(prev => ({ ...prev, [key]: prev[key] ?? '' }));
  };

  const [customKeys, setCustomKeys] = useState([]);
  const [editHidden, setEditHidden] = useState({});
  const [editStructured, setEditStructured] = useState({});
  const [editHelp, setEditHelp] = useState({});
  const [editAlias, setEditAlias] = useState({});
  const addCustomKey = () => {
    setCustomKeys(prev => [...prev, { id: Date.now(), key: '', value: '' }]);
  };

  const deleteParam = (key) => {
    if (extraKeys.includes(key)) {
      setExtraKeys(prev => prev.filter(k => k !== key));
    } else {
      setDeletedKeys(prev => [...prev, key]);
    }
    setEditValues(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const token = await getAccessToken();

      const settings = {};
      const settingsConfig = {};
      const allKeys = [...defaultSettings.filter(s => !deletedKeys.includes(s.key)).map(s => s.key), ...extraKeys];
      const validCustomKeys = customKeys.filter(c => c.key.trim());
      const builtinKeys = allKeys.filter(k => BUILTIN_PARAMS.includes(k));
      const otherKeys = [...allKeys.filter(k => !BUILTIN_PARAMS.includes(k)), ...validCustomKeys.map(c => c.key.trim())];
      builtinKeys.sort((a, b) => BUILTIN_PARAMS.indexOf(a) - BUILTIN_PARAMS.indexOf(b));

      builtinKeys.forEach((key) => {
        settings[key] = editValues[key] ?? '';
        settingsConfig[key] = {
          Order: BUILTIN_PARAMS.indexOf(key),
          Help: editHelp[key] || '',
          Alias: editAlias[key] || '',
          Hidden: editHidden[key] ?? false,
          Structured: editStructured[key] ?? false,
        };
      });
      otherKeys.forEach((key, index) => {
        const custom = validCustomKeys.find(c => c.key.trim() === key);
        settings[key] = custom ? custom.value : (editValues[key] ?? '');
        settingsConfig[key] = {
          Order: BUILTIN_PARAMS.length + index,
          Help: custom ? (custom.help || '') : (editHelp[key] || ''),
          Alias: custom ? (custom.alias || '') : (editAlias[key] || ''),
          Hidden: custom ? (custom.hidden ?? false) : (editHidden[key] ?? false),
          Structured: custom ? (custom.structured ?? false) : (editStructured[key] ?? false),
        };
      });

      const resp = await fetch(
        `${apiConfig.apiBaseUrl}/polaris/test-types/${encodeURIComponent(testType.Id)}/settings`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ settings, settingsConfig }),
        }
      );
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to save settings: ${resp.status}`);
      }
      await fetchDefaultSettings();
      if (onTestTypeUpdated) onTestTypeUpdated({ ...testType, ClientSettingConfig: JSON.stringify(settingsConfig) });
      setEditing(false);
      setCustomKeys([]);
      setSaveSuccess('Settings saved successfully.');
    } catch (err) {
      setSaveError(err.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const startResultEditing = () => {
    let existingConfig = { TestRunNameLabel: 'QPS', Counters: {} };
    try {
      if (testType.ClientResultConfig) {
        const parsed = JSON.parse(testType.ClientResultConfig);
        existingConfig = { ...existingConfig, ...parsed };
        if (!existingConfig.Counters) existingConfig.Counters = {};
      }
    } catch { /* ignore */ }

    setResultLabel(existingConfig.TestRunNameLabel || 'QPS');

    const counterKeys = latestCounters ? Object.keys(latestCounters) : Object.keys(existingConfig.Counters);
    const counters = counterKeys.map((key, idx) => ({
      key,
      value: latestCounters?.[key] ?? null,
      decimalPlace: existingConfig.Counters[key]?.DecimalPlace ?? 6,
      inSummary: existingConfig.Counters[key]?.InSummary ?? false,
      name: existingConfig.Counters[key]?.Name || '',
      description: existingConfig.Counters[key]?.Description || '',
      order: existingConfig.Counters[key]?.Order ?? idx,
    }));
    counters.sort((a, b) => a.order - b.order);
    setResultCounters(counters);
    setResultEditing(true);
    setResultError(null);
    setResultSuccess(null);
  };

  const cancelResultEditing = () => {
    setResultEditing(false);
    setResultError(null);
  };

  const moveResultCounter = (index, direction) => {
    const newCounters = [...resultCounters];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newCounters.length) return;
    [newCounters[index], newCounters[targetIndex]] = [newCounters[targetIndex], newCounters[index]];
    setResultCounters(newCounters);
  };

  const updateResultCounter = (index, field, value) => {
    setResultCounters(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const handleSaveResultConfig = async () => {
    setResultSaving(true);
    setResultError(null);
    setResultSuccess(null);
    try {
      const config = {
        TestRunNameLabel: resultLabel || 'QPS',
        Counters: {},
        Links: {},
      };
      resultCounters.forEach((c, idx) => {
        config.Counters[c.key] = {
          Order: idx,
          Name: c.name,
          Description: c.description,
          DecimalPlace: parseInt(c.decimalPlace, 10) || 0,
          InSummary: c.inSummary,
        };
      });

      const token = await getAccessToken();
      const resp = await fetch(
        `${apiConfig.apiBaseUrl}/polaris/test-types/${encodeURIComponent(testType.Id)}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ClientResultConfig: JSON.stringify(config) }),
        }
      );
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to save: ${resp.status}`);
      }
      setResultEditing(false);
      setResultSuccess('Result config saved successfully.');
      if (onTestTypeUpdated) onTestTypeUpdated({ ...testType, ClientResultConfig: JSON.stringify(config) });
    } catch (err) {
      setResultError(err.message || 'Failed to save result config.');
    } finally {
      setResultSaving(false);
    }
  };

  let defaultSettings = [];
  if (testType.ClientSettingConfig) {
    try {
      const parsed = JSON.parse(testType.ClientSettingConfig);
      defaultSettings = Object.entries(parsed)
        .map(([key, config]) => ({ key, ...config }))
        .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
    } catch {
      // ignore parse errors
    }
  }

  const availableBuiltinParams = BUILTIN_PARAMS
    .filter(k => !defaultSettings.some(s => s.key === k) && !extraKeys.includes(k));

  return (
    <div className={classes.container}>
      <div className={classes.detailScrollContent}>
      <div className={classes.detailTopSection}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <Subtitle2>TestType: {testType.Id}</Subtitle2>
          {metaEditing ? (
            <div className={classes.actionGroupCompact}>
              <Button appearance="primary" onClick={handleMetaSave} disabled={metaSaving}>
                {metaSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button appearance="subtle" onClick={cancelMetaEditing} disabled={metaSaving}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button appearance="subtle" icon={<Edit20Regular />} onClick={startMetaEditing}>
              Edit
            </Button>
          )}
        </div>
        {metaError && (
          <MessageBar intent="error" className={classes.messageBarCompact}>
            <MessageBarBody>{metaError}</MessageBarBody>
          </MessageBar>
        )}
        {metaSuccess && (
          <MessageBar intent="success" className={classes.messageBarCompact}>
            <MessageBarBody>{metaSuccess}</MessageBarBody>
          </MessageBar>
        )}
        <div className={classes.detailFormGrid}>
          <Card className={classes.detailSectionCard}>
            <Subtitle2>Required Details</Subtitle2>
            <div className={classes.detailSectionBody}>
              <div className={classes.detailFieldsGrid}>
                {requiredDetailFields.map(([label, value]) => renderDetailField(label, value))}
              </div>
            </div>
          </Card>

          <Card className={classes.detailSectionCard}>
            <Subtitle2>Execution Settings</Subtitle2>
            <div className={classes.detailSectionBody}>
              <div className={classes.detailFieldsGrid}>
                {executionDetailFields.map(([label, value]) => renderDetailField(label, value))}
              </div>
            </div>
          </Card>

          <Card className={classes.detailSectionCard}>
            <Subtitle2>Feature Flags</Subtitle2>
            <div className={classes.detailSectionBody}>
              <div className={classes.detailCheckboxGrid}>
                {featureFlagFields.map(([label, value]) => renderDetailCheckboxField(label, value))}
              </div>
            </div>
          </Card>

          <Card className={classes.detailSectionCard}>
            <Subtitle2>Optional Settings</Subtitle2>
            <div className={classes.detailSectionBody}>
              <div className={classes.detailFieldsGrid}>
                {optionalDetailFields.map(([label, value]) => renderDetailField(label, value))}
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Card className={mergeClasses(classes.cardTopMargin, classes.defaultSettingsCard)}>
        <CardHeader
          header={<Subtitle2>Default settings</Subtitle2>}
          action={
            editing ? (
              <div className={classes.actionGroupWrap}>
                {availableBuiltinParams.length === 0 ? (
                  <Button appearance="subtle" icon={<Add20Regular />} onClick={addCustomKey}>
                    Add
                  </Button>
                ) : (
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button appearance="subtle" icon={<Add20Regular />}>
                        Add
                      </Button>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem onClick={addCustomKey}>Custom Key</MenuItem>
                        <MenuDivider />
                        {availableBuiltinParams.map(k => (
                          <MenuItem key={k} onClick={() => addBuiltinParam(k)}>
                            {`Add ${k}`}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                )}
                <Button appearance="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button appearance="subtle" onClick={cancelEditing} disabled={saving}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button appearance="subtle" icon={<Edit20Regular />} onClick={startEditing}>
                Edit
              </Button>
            )
          }
        />
        {saveError && (
          <MessageBar intent="error" className={classes.messageBarCompact}>
            <MessageBarBody>{saveError}</MessageBarBody>
          </MessageBar>
        )}
        {saveSuccess && (
          <MessageBar intent="success" className={classes.messageBarCompact}>
            <MessageBarBody>{saveSuccess}</MessageBarBody>
          </MessageBar>
        )}
        {(defaultSettings.length > 0 || (editing && (extraKeys.length > 0 || customKeys.length > 0))) ? (
          <>
            <div className={classes.settingsTableContainer}>
            <table className={classes.settingsTable}>
              <thead>
                <tr>
                  <th className={mergeClasses(classes.settingsHeader, classes.settingsKeyColumn)}>Key</th>
                  <th className={classes.settingsHeader}>Value</th>
                  <th className={classes.settingsHeader}>Help</th>
                  <th className={classes.settingsHeader}>Alias</th>
                  <th className={classes.settingsHeader}>Hidden</th>
                  <th className={classes.settingsHeader}>Structured</th>
                  {editing && <th className={classes.settingsHeader}></th>}
                </tr>
              </thead>
              <tbody>
                {defaultSettings.filter(s => !deletedKeys.includes(s.key)).map((s) => (
                  <tr key={s.key}>
                    <td className={mergeClasses(classes.settingsTd, classes.settingsKeyColumn)}>{s.key}</td>
                    <td className={classes.settingsTd}>
                      {editing ? renderEditInput(s.key) : (defaultValues[s.key] ?? '')}
                    </td>
                    <td className={classes.settingsTd}>
                      {editing ? (
                        <Input className={classes.fullWidthField} value={editHelp[s.key] ?? ''} onChange={(_e, d) => setEditHelp(prev => ({ ...prev, [s.key]: d.value }))} />
                      ) : (s.Help || '')}
                    </td>
                    <td className={classes.settingsTd}>
                      {editing ? (
                        <Input className={classes.fullWidthField} value={editAlias[s.key] ?? ''} onChange={(_e, d) => setEditAlias(prev => ({ ...prev, [s.key]: d.value }))} />
                      ) : (s.Alias || '')}
                    </td>
                    <td className={classes.settingsTd}>
                      {editing ? (
                        <Checkbox checked={editHidden[s.key] ?? false} onChange={(_e, d) => setEditHidden(prev => ({ ...prev, [s.key]: d.checked }))} />
                      ) : (
                        <Checkbox checked={s.Hidden ?? false} disabled />
                      )}
                    </td>
                    <td className={classes.settingsTd}>
                      {editing ? (
                        <Checkbox checked={editStructured[s.key] ?? false} onChange={(_e, d) => setEditStructured(prev => ({ ...prev, [s.key]: d.checked }))} />
                      ) : (
                        <Checkbox checked={s.Structured ?? false} disabled />
                      )}
                    </td>
                    {editing && (
                      <td className={classes.settingsTd}>
                        <Button appearance="subtle" icon={<Dismiss20Regular />} onClick={() => deleteParam(s.key)} />
                      </td>
                    )}
                  </tr>
                ))}
                {editing && extraKeys.map((key) => (
                  <tr key={key}>
                    <td className={mergeClasses(classes.settingsTd, classes.settingsKeyColumn)}>{key}</td>
                    <td className={classes.settingsTd}>
                      {renderEditInput(key)}
                    </td>
                    <td className={classes.settingsTd}>
                      <Input className={classes.fullWidthField} value={editHelp[key] ?? ''} onChange={(_e, d) => setEditHelp(prev => ({ ...prev, [key]: d.value }))} />
                    </td>
                    <td className={classes.settingsTd}>
                      <Input className={classes.fullWidthField} value={editAlias[key] ?? ''} onChange={(_e, d) => setEditAlias(prev => ({ ...prev, [key]: d.value }))} />
                    </td>
                    <td className={classes.settingsTd}>
                      <Checkbox checked={editHidden[key] ?? false} onChange={(_e, d) => setEditHidden(prev => ({ ...prev, [key]: d.checked }))} />
                    </td>
                    <td className={classes.settingsTd}>
                      <Checkbox checked={editStructured[key] ?? false} onChange={(_e, d) => setEditStructured(prev => ({ ...prev, [key]: d.checked }))} />
                    </td>
                    <td className={classes.settingsTd}>
                      <Button appearance="subtle" icon={<Dismiss20Regular />} onClick={() => deleteParam(key)} />
                    </td>
                  </tr>
                ))}
                {editing && customKeys.map((item) => (
                  <tr key={item.id}>
                    <td className={mergeClasses(classes.settingsTd, classes.settingsKeyColumn)}>
                      <Input
                        className={classes.fullWidthField}
                        placeholder="Key"
                        value={item.key}
                        onChange={(_e, d) => setCustomKeys(prev => prev.map(c => c.id === item.id ? { ...c, key: d.value } : c))}
                      />
                    </td>
                    <td className={classes.settingsTd}>
                      <Input
                        className={classes.fullWidthField}
                        placeholder="Value"
                        value={item.value}
                        onChange={(_e, d) => setCustomKeys(prev => prev.map(c => c.id === item.id ? { ...c, value: d.value } : c))}
                      />
                    </td>
                    <td className={classes.settingsTd}>
                      <Input className={classes.fullWidthField} value={item.help ?? ''} onChange={(_e, d) => setCustomKeys(prev => prev.map(c => c.id === item.id ? { ...c, help: d.value } : c))} />
                    </td>
                    <td className={classes.settingsTd}>
                      <Input className={classes.fullWidthField} value={item.alias ?? ''} onChange={(_e, d) => setCustomKeys(prev => prev.map(c => c.id === item.id ? { ...c, alias: d.value } : c))} />
                    </td>
                    <td className={classes.settingsTd}>
                      <Checkbox checked={item.hidden ?? false} onChange={(_e, d) => setCustomKeys(prev => prev.map(c => c.id === item.id ? { ...c, hidden: d.checked } : c))} />
                    </td>
                    <td className={classes.settingsTd}>
                      <Checkbox checked={item.structured ?? false} onChange={(_e, d) => setCustomKeys(prev => prev.map(c => c.id === item.id ? { ...c, structured: d.checked } : c))} />
                    </td>
                    <td className={classes.settingsTd}>
                      <Button appearance="subtle" icon={<Dismiss20Regular />} onClick={() => setCustomKeys(prev => prev.filter(c => c.id !== item.id))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        ) : (
          <Text className={classes.emptyStateText}>
            No default settings configured.
          </Text>
        )}
      </Card>

      <Card>
        <CardHeader
          header={<Subtitle2>Customize Results</Subtitle2>}
          action={
            (testType.ClientResultConfig || latestCounters) && (
              resultEditing ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button appearance="primary" onClick={handleSaveResultConfig} disabled={resultSaving}>
                    {resultSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button appearance="subtle" onClick={cancelResultEditing} disabled={resultSaving}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button appearance="subtle" icon={<Edit20Regular />} onClick={startResultEditing}>
                  Edit
                </Button>
              )
            )
          }
        />
        {resultError && (
          <MessageBar intent="error" className={classes.messageBarCompact}>
            <MessageBarBody>{resultError}</MessageBarBody>
          </MessageBar>
        )}
        {resultSuccess && (
          <MessageBar intent="success" className={classes.messageBarCompact}>
            <MessageBarBody>{resultSuccess}</MessageBarBody>
          </MessageBar>
        )}
        {!testType.ClientResultConfig && !latestCounters ? (
          <Text className={classes.emptyStateText}>
            No result configuration yet. Configuration will be available once results are generated.
          </Text>
        ) : resultEditing ? (
          <>
            <div className={classes.settingsTableContainer}>
              <table className={classes.settingsTable}>
                <thead>
                  <tr>
                    <th className={classes.settingsHeader} colSpan={7}>Test Run Name Label</th>
                  </tr>
                  <tr>
                    <td className={classes.settingsTd} colSpan={7}>
                      <Input value={resultLabel} onChange={(_e, d) => setResultLabel(d.value)} placeholder="QPS" style={{ width: '200px' }} />
                    </td>
                  </tr>
                  <tr>
                    <th className={classes.settingsHeader}></th>
                    <th className={classes.settingsHeader}>Counter</th>
                    <th className={classes.settingsHeader}>Value</th>
                    <th className={classes.settingsHeader}>Decimal Places</th>
                    <th className={classes.settingsHeader}>InSummary</th>
                    <th className={classes.settingsHeader}>Name</th>
                    <th className={classes.settingsHeader}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {resultCounters.map((c, idx) => (
                    <tr key={c.key}>
                      <td className={classes.settingsTd}>
                        <Button appearance="subtle" icon={<ArrowUp20Regular />} size="small" disabled={idx === 0} onClick={() => moveResultCounter(idx, -1)} />
                        <Button appearance="subtle" icon={<ArrowDown20Regular />} size="small" disabled={idx === resultCounters.length - 1} onClick={() => moveResultCounter(idx, 1)} />
                      </td>
                      <td className={classes.settingsTd}>{c.key}</td>
                      <td className={classes.settingsTd}>{c.value != null ? Number(c.value).toFixed(6) : ''}</td>
                      <td className={classes.settingsTd}>
                        <Input type="number" min={0} max={6} value={String(c.decimalPlace)} onChange={(_e, d) => updateResultCounter(idx, 'decimalPlace', d.value)} style={{ width: '60px' }} />
                      </td>
                      <td className={classes.settingsTd}>
                        <Checkbox checked={c.inSummary} onChange={(_e, d) => updateResultCounter(idx, 'inSummary', d.checked)} />
                      </td>
                      <td className={classes.settingsTd}>
                        <Input className={classes.fullWidthField} value={c.name} onChange={(_e, d) => updateResultCounter(idx, 'name', d.value)} />
                      </td>
                      <td className={classes.settingsTd}>
                        <Input className={classes.fullWidthField} value={c.description} onChange={(_e, d) => updateResultCounter(idx, 'description', d.value)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
          <div className={classes.settingsTableContainer}>
            <table className={classes.settingsTable}>
              <thead>
                <tr>
                  <th className={classes.settingsHeader} colSpan={6}>Test Run Name Label</th>
                </tr>
                <tr>
                  <td className={classes.settingsTd} colSpan={6}>{(() => {
                    try { if (testType.ClientResultConfig) return JSON.parse(testType.ClientResultConfig).TestRunNameLabel || 'QPS'; } catch {}
                    return 'QPS';
                  })()}</td>
                </tr>
                <tr>
                  <th className={classes.settingsHeader}>Counter</th>
                  <th className={classes.settingsHeader}>Value</th>
                  <th className={classes.settingsHeader}>Decimal Places</th>
                  <th className={classes.settingsHeader}>InSummary</th>
                  <th className={classes.settingsHeader}>Name</th>
                  <th className={classes.settingsHeader}>Description</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let config = { Counters: {} };
                  try { if (testType.ClientResultConfig) config = { ...config, ...JSON.parse(testType.ClientResultConfig) }; } catch {}
                  if (!config.Counters) config.Counters = {};
                  const configKeys = Object.keys(config.Counters);
                  let keys;
                  if (latestCounters) {
                    const actualKeys = Object.keys(latestCounters);
                    keys = configKeys.length > 0
                      ? actualKeys.sort((a, b) => (config.Counters[a]?.Order ?? 9999) - (config.Counters[b]?.Order ?? 9999))
                      : actualKeys;
                  } else {
                    keys = configKeys.sort((a, b) => (config.Counters[a]?.Order ?? 9999) - (config.Counters[b]?.Order ?? 9999));
                  }
                  return keys.map(key => (
                    <tr key={key}>
                      <td className={classes.settingsTd}>{key}</td>
                      <td className={classes.settingsTd}>{latestCounters?.[key] != null ? Number(latestCounters[key]).toFixed(6) : ''}</td>
                      <td className={classes.settingsTd}>{config.Counters[key]?.DecimalPlace ?? 6}</td>
                      <td className={classes.settingsTd}>{config.Counters[key]?.InSummary ? 'Yes' : 'No'}</td>
                      <td className={classes.settingsTd}>{config.Counters[key]?.Name || ''}</td>
                      <td className={classes.settingsTd}>{config.Counters[key]?.Description || ''}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>
      </div>
    </div>
  );
}

function PolarisTestTypesPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken } = useAuth();
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();

  const pathParts = location.pathname.replace(/\/$/, '').split('/');
  const testTypeId = pathParts.length > 3 ? decodeURIComponent(pathParts.slice(3).join('/')) : null;

  const [searchText, setSearchText] = useState('');

  const testTypesQuery = useQuery({
    queryKey: ['polaris-test-types'],
    queryFn: async () => {
      const token = await getAccessToken();
      const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/test-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`API error: ${resp.status}`);
      return resp.json();
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const testTypes = testTypesQuery.data ?? [];
  const loading = testTypesQuery.isLoading;
  const error = testTypesQuery.error?.message ?? null;

  const filteredTestTypes = useMemo(() => {
    let list = testTypes;
    if (searchText) {
      const lower = searchText.toLowerCase();
      list = list.filter(
        (t) =>
          (t.Id && t.Id.toLowerCase().includes(lower)) ||
          (t.TaskType && t.TaskType.toLowerCase().includes(lower))
      );
    }
    const enabled = list.filter((t) => t.IsEnabled).sort((a, b) => a.Id.localeCompare(b.Id));
    const disabled = list.filter((t) => !t.IsEnabled).sort((a, b) => a.Id.localeCompare(b.Id));
    return [...enabled, ...disabled];
  }, [testTypes, searchText]);

  if (testTypeId) {
    const testType = testTypes.find(
      (t) => t.Id.toLowerCase() === testTypeId.toLowerCase()
    );
    if (loading) {
      return (
        <div className={classes.container}>
          <div className={classes.loading}>
            <Spinner size="large" label="Loading test types..." />
          </div>
        </div>
      );
    }
    if (!testType) {
      return (
        <div className={classes.container}>
          <Text className={classes.errorText}>
            Test type &quot;{testTypeId}&quot; not found.
          </Text>
        </div>
      );
    }
    return (
      <TestTypeDetail
        testType={testType}
        onBack={() => navigate('/polaris/test-types')}
        getAccessToken={getAccessToken}
        onTestTypeUpdated={(updated) => {
          queryClient.setQueryData(['polaris-test-types'], (prev) =>
            prev ? prev.map(t => t.Id === updated.Id ? { ...t, ...updated } : t) : prev
          );
        }}
      />
    );
  }

  const columns = [
    createTableColumn({
      columnId: 'Id',
      renderHeaderCell: () => 'Id',
      renderCell: (item) => (
        <Tooltip content={item.Id || ''} relationship="label">
          <span className={classes.cellContent}>{item.Id}</span>
        </Tooltip>
      ),
      compare: (a, b) => (a.Id || '').localeCompare(b.Id || ''),
    }),
    createTableColumn({
      columnId: 'TaskType',
      renderHeaderCell: () => 'TaskType',
      renderCell: (item) => (
        <Tooltip content={item.TaskType || ''} relationship="label">
          <span className={classes.cellContent}>{item.TaskType}</span>
        </Tooltip>
      ),
      compare: (a, b) => (a.TaskType || '').localeCompare(b.TaskType || ''),
    }),
    createTableColumn({
      columnId: 'Actions',
      renderHeaderCell: () => 'Action',
      renderCell: (item) => (
        <Button
          appearance="subtle"
          icon={<Eye20Regular />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/polaris/test-types/${item.Id}`);
          }}
        >
          View testType
        </Button>
      ),
    }),
  ];

  const columnSizingOptions = {
    Id: { minWidth: 180, defaultWidth: 320 },
    TaskType: { minWidth: 180, defaultWidth: 320 },
    Actions: { minWidth: 180, defaultWidth: 320 },
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Toolbar className={classes.toolbar}>
          <ToolbarGroup>
            {isAdmin && (
              <ToolbarButton icon={<Add20Regular />} onClick={() => navigate('/polaris/test-types/create')}>
                Create TestType
              </ToolbarButton>
            )}
          </ToolbarGroup>
          <ToolbarGroup className={classes.toolbarFarGroup}>
            <SearchBox
              className={classes.searchInput}
              placeholder="Search Test Type"
              value={searchText}
              onChange={(e, d) => setSearchText(d.value)}
            />
          </ToolbarGroup>
        </Toolbar>
      </div>

      <div className={classes.contentWrapper}>
        {error && (
          <MessageBar intent="error" className={classes.errorBanner}>
            <MessageBarBody>Error: {error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={classes.listSection}>
        <Card className={classes.listCard}>
          <CardHeader
            header={
              <div className={classes.listCardHeader}>
                <Subtitle2>TestType</Subtitle2>
                <div className={classes.listCardHeaderSpacer} />
                <Text size={200} className={classes.countInfo}>
                  {filteredTestTypes.length} of {testTypes.length} entries
                </Text>
              </div>
            }
          />
          <div className={classes.listCardBody}>
            {loading ? (
              <div className={classes.loading}>
                <Spinner label="Loading..." />
              </div>
            ) : filteredTestTypes.length === 0 ? (
              <Text align="center" className={classes.emptyStateText}>
                No test types found
              </Text>
            ) : (
              <div className={classes.listScrollBody}>
                <DataGrid
                  items={filteredTestTypes}
                  columns={columns}
                  getRowId={(item) => item.Id}
                  sortable
                  resizableColumns
                  columnSizingOptions={columnSizingOptions}
                  size="extra-small"
                  className={classes.dataGridCompact}
                >
                  <DataGridHeader className={classes.stickyGridHeader}>
                    <DataGridRow className={classes.headerRow}>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell className={classes.headerCell}>
                          {renderHeaderCell()}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody>
                    {({ item, rowId }) => (
                      <DataGridRow key={rowId} className={mergeClasses(classes.dataRow, !item.IsEnabled && classes.disabledRow)} onClick={() => navigate(`/polaris/test-types/${item.Id}`)}>
                        {({ renderCell }) => (
                          <DataGridCell className={classes.dataCell}>
                            {renderCell(item)}
                          </DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              </div>
            )}
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
}

export default PolarisTestTypesPage;
